import { useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { TextField } from '@bloo-team/ui'
import { readCallSpec, writeCallSpec } from '@bloo-team/api-client'
import { useWorkbookPage } from '../../../lib/detection-list'
import type { BlockEditorProps, BlockViewProps } from './types'

/**
 * Call / Return: the block names another workbook. Captured shape
 * (workbook-blocks-capture.md): `query` = the target workbook's id, `name` /
 * `path` = its name and folder. The picker searches `workbook/list` by
 * `name LIKE`.
 */

export function CallView({ block, clusterId, scopeId }: BlockViewProps) {
  const spec = readCallSpec(block)
  if (!spec.workbookId) return <p className="hud-unit">No workbook chosen yet.</p>
  // Just the target: its name (linked) and folder — the card's label already says Call / Return.
  return (
    <div className="wb-target">
      <Link
        to="/$clusterId/$scopeId/vantage/detections/workbook"
        params={{ clusterId, scopeId }}
        search={{ wb: spec.workbookId }}
      >
        {spec.name || spec.workbookId}
      </Link>
      {spec.path ? <span className="bui-chip wb-chip-mono">{spec.path}</span> : null}
    </div>
  )
}

export function CallEditor({ block, clusterId, scopeId, index, onChange }: BlockEditorProps) {
  const spec = readCallSpec(block)
  const [text, setText] = useState('')
  const [term, setTerm] = useState('')
  // Debounced: one list call per pause in typing, not per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 300)
    return () => clearTimeout(t)
  }, [text])
  const page = useWorkbookPage({
    clusterId,
    scopeId,
    pageNo: 1,
    pageSize: 8,
    filters: [{ key: 'name', operand: 'LIKE', value: term }],
    enabled: term.length >= 2,
  })
  const options = term.length >= 2 ? (page.data?.rows ?? []) : []

  return (
    <div className="hud-stack wb-gap-2">
      <CallView block={block} clusterId={clusterId} scopeId={scopeId} />
      <div className="bui-combo wb-w-lg">
        <TextField
          label={`Block ${index + 1} target workbook`}
          placeholder="search workbooks by name…"
          value={text}
          onChange={setText}
        />
        {options.length > 0 ? (
          <div className="bui-combo-list" role="listbox" aria-label="Matching workbooks">
            {options.map((w) => (
              <button
                key={w.id}
                type="button"
                role="option"
                aria-selected={w.id === spec.workbookId}
                className="bui-combo-opt wb-combo-opt"
                onClick={() => {
                  onChange(writeCallSpec(block, { workbookId: w.id, name: w.name, path: w.path }))
                  setText('')
                  setTerm('')
                }}
              >
                <span>{w.name}</span>
                <span className="mono muted">{w.path}</span>
              </button>
            ))}
          </div>
        ) : term.length >= 2 && page.isFetched ? (
          <p className="hud-unit">No workbook matches “{term}”.</p>
        ) : null}
      </div>
    </div>
  )
}
